import {Country, LastOrderDraft, OrderCustomer, OrderForm, Timetable, TimetableDate} from '~/types/types';
import {Ref} from 'vue';
import {useCityStore} from '~/stores/city';
import {useSessionStorage} from '@vueuse/core';
import {deepCopyObject} from '~/common/functions';
import {
  convertPriceServices,
  convertScheduller,
  defaultOrder,
  getCountryFlagIcon,
  getCurrencyText,
  getTimetablePayload,
  convertTimeTableV3,
  pricePayload, getSplitIndex
} from '~/common/objects';
import {getOrderPrice as getOrderPriceApi, getTimeTable as getTimeTableApi, orderCreate} from '~/api/order';
import {formatDate} from '~/common/date';
import {RouteLocationRaw} from 'vue-router';
import {LAST_ORDER_DRAFT_KEY} from '~/types/order/order';

type PriceItem = {
  currencyMeasure: string
  currency: Country
  flag: string
  base: number
  total: number
  discount: number
}

/**
 * Использовать только в orderStore.
 * Файл предназначен для разделения логики заказа и мультизаявки, несмотря на то что они очень связаны друг с другом
 * @param orderForm
 */
const useMultiOrder = (orderForm: Ref<OrderForm>) => {
  const { $showToast } = useNuxtApp()
  const { rclCityList } = useCityStore()

  const storageMultiOrderData = useSessionStorage<LastOrderDraft>(
      LAST_ORDER_DRAFT_KEY,
      null,
      { serializer: {read: JSON.parse, write: JSON.stringify} } // иначе не работает
  )

  /**
   *  Список заявок.
   *  Содержат данные о прибытии (весь OrderForm кроме dispatch (directions, customer, specialRequirements)).
   *  В meta:
   *  index - для работы с requestAsOrderList но ссылкой на requestList по индексу
   *  type - тип заказа индивидуальный/торговые сети
   *  error - ошибка при getPrice(test),
   *  loading - статус загрузки (цена/оформление)
   *  temp - заявка создаётся
   *  */
  const requestList = ref<Array<Partial<OrderForm>>>([])
  const selectedRequestId = ref<number>(0)
  const editMultiOrderMode = ref(true)
  const timetableSplitList = ref<number[]>([])
  const timetableCalcIndex = ref(0)

  // Внутри, начинается с 0
  const selectedRequestKey = computed(() => {
    return selectedRequestId.value - 1
  })

  const selectedRequest = computed(() => {
    return requestList.value[selectedRequestKey.value]
  })

  const requestAsOrderList = computed(() => {
    // Сначала map потом filter, чтоб index работал
    return deepCopyObject(requestList.value as OrderForm[])
        .map((order, index) => {
          order.directions.dispatch = deepCopyObject(orderForm.value.directions.dispatch)
          order.customer.dispatch = deepCopyObject(orderForm.value.customer.dispatch)
          order.specialRequirements.dispatch = deepCopyObject(orderForm.value.specialRequirements.dispatch)
          order.meta.index = index
          return order
        })
        .filter(order => !order.meta.id)
  })

  const locked = computed(() => {
    return !!requestList.value.length && requestList.value.length !== requestAsOrderList.value.length
  })

  const priceList = computed((): Array<PriceItem> => {
    return requestList.value
        .map(order => {
          const currency = Object.keys(order.totalPrice?.currency || {})
              .find(key => order.totalPrice.currency[key] === true) as Country || 'RU'
          const currencyMeasure = getCurrencyText(currency, currency === 'RU')
          const flag = getCountryFlagIcon(currency)

          return {
            ...order.totalPrice,
            currencyMeasure,
            currency,
            flag
          }
        })
  })

  const totalPriceList = computed(() => {
    const result = {} as Record<Country, PriceItem>

    priceList.value.forEach(price => {
      if (!result[price.currency]) {
        result[price.currency] = {
          total: 0,
          base: 0,
          discount: 0,
          flag: price.flag,
          currencyMeasure: price.currencyMeasure,
          currency: price.currency
        }
      }
      result[price.currency].base += price.base
      result[price.currency].total += price.total
      result[price.currency].discount += +price.discount
    })

    return result
  })

  const multiPickupRoute = computed((): RouteLocationRaw => {
    if (orderForm.value.directions.dispatch.direction !== 'address') {
      return ''
    }

    // const payerType = orderForm.value.customer.payer
    // Так в 1С приходит для группового, без разницы что в заказе
    const payerType = 'dispatch'
    const payer = orderForm.value.customer[payerType] as OrderCustomer
    const payerGuid = payer?.corporation?.companyName?.id || payer.individual.name.id

    return {
      name: 'personal-multi-pickup-guid',
      params: { guid: 'create' },
      query: {
        location: orderForm.value.directions.dispatch.locationGuid,
        date: formatDate(orderForm.value.directions.dispatch.calendar.date),
        payer: payerGuid
      }
    }
  })

  function addRequest(order: Partial<OrderForm>|null = null) {
    setToStorage()
    let localOrder = order as OrderForm
    if (!localOrder) {
      localOrder = deepCopyObject(defaultOrder)
      localOrder.directions.destination.locationGuid = rclCityList
          .find(city => city.guid !== orderForm.value.directions.dispatch.locationGuid)?.guid || ''
      localOrder.directions.destination.calendar = deepCopyObject(orderForm.value.directions.destination.calendar)
    }
    localOrder.meta.temp = true

    requestList.value.push(deepCopyObject(localOrder))
    setData(localOrder)
    selectedRequestId.value = requestList.value.length
  }

  function updateRequest(order: OrderForm, index: number|null = null) {
    const localOrder = deepCopyObject(order)
    const localIndex = index === null ? selectedRequestKey.value : index

    if (localOrder.directions.dispatch.calendar.terminalTime) {
      localOrder.directions.dispatch.calendar.terminalTime = storageMultiOrderData.value?.order?.directions.dispatch.calendar.terminalTime
          || localOrder.directions.dispatch.calendar.terminalTime
      orderForm.value.directions.dispatch.calendar.terminalTime = localOrder.directions.dispatch.calendar.terminalTime
          || orderForm.value.directions.dispatch.calendar.terminalTime
    }

    requestList.value[localIndex] = localOrder
    setToStorage()
  }

  function saveRequest(data?: Timetable['destination']) {
    const terminalSplit = data?.map(tt => tt.groupTimeMin)
    updateRequest({
      ...orderForm.value,
      meta: {...orderForm.value.meta, terminalSplit, temp: false, error: false}
    })
    $showToast({
      title: `Заявка ${selectedRequestId.value} сохранена`,
    })
    selectedRequestId.value = 0
  }

  function deleteRequest(index: number) {
    requestList.value.splice(index - 1, 1)
    setToStorage()
  }

  function setToStorage() {
    storageMultiOrderData.value = {
      type: 'multi',
      order: storageMultiOrderData.value?.order || orderForm.value,
      list: requestList.value
    }
  }

  function getFromStorage() {
    requestList.value = deepCopyObject(storageMultiOrderData.value?.list || []).filter(request => !request.meta.temp)
    if (storageMultiOrderData.value?.order) {
      orderForm.value = deepCopyObject(storageMultiOrderData.value.order)
    }
    if (selectedRequest.value) {
      setData(selectedRequest.value)
    }
    editMultiOrderMode.value = false
  }

  function setData(order: Partial<OrderForm>) {
    const form = deepCopyObject(order) as OrderForm
    form.directions.dispatch = deepCopyObject(orderForm.value.directions.dispatch)
    form.customer.dispatch = deepCopyObject(orderForm.value.customer.dispatch)
    form.specialRequirements.dispatch = deepCopyObject(orderForm.value.specialRequirements.dispatch)
    orderForm.value = form
    editMultiOrderMode.value = false
  }

  async function getMultiPrice() {
    await Promise.all(requestAsOrderList.value.map(async (request) => {
      await getRequestPrice(request.meta.index)
    }))

    storageMultiOrderData.value = {
      type: 'multi',
      order: orderForm.value,
      list: requestList.value
    }
  }

  async function getRequestPrice(index: number) {
    const request = requestList.value[index]
    const order = deepCopyObject(request) as OrderForm

    request.meta.loading = true
    delete(request.meta.error)

    order.directions.dispatch = deepCopyObject(orderForm.value.directions.dispatch)
    order.customer.dispatch = deepCopyObject(orderForm.value.customer.dispatch)
    order.specialRequirements.dispatch = deepCopyObject(orderForm.value.specialRequirements.dispatch)

    delete(order.meta.error)
    const data = await getOrderPriceApi(pricePayload(order))

    if (Object.keys(data).length === 1 && 'statusCode' in data) {
      data.error = {
        opt: { message: { message: 'Неизвестная ошибка', description: `Статус: ${data.statusCode}` } }
      }
    }

    if (data.error) {
      $showToast({
        variant: 'error',
        title: data.error.opt.message?.message || data.error.message,
        text: data.error.opt.message?.description,
        iconName: 'alert'
      })
      order.meta.error = data.error.opt.message?.message || data.error.message
    } else {
      order.services = convertPriceServices(data)
      if (data.shippingDiagram) {
        order.shippingDiagram = convertScheduller(data.shippingDiagram)
      }
      if (data.cost) {
        order.totalPrice = data.cost
      }
    }

    // Устанавливаем изменённые цены
    requestList.value[index].meta.loading = false
    requestList.value[index].meta.error = order.meta.error
    requestList.value[index].services = order.services
    requestList.value[index].shippingDiagram = order.shippingDiagram
    requestList.value[index].totalPrice = order.totalPrice
  }

  async function sendMultiFormTest(phone: any): Promise<{ reject?: string }> {
    const res = await Promise.all(requestAsOrderList.value.map(async (form) => {
      const index = form.meta.index
      delete(requestList.value[index].meta.error)

      const requestResult = await orderCreate({...pricePayload(form), phone}, true)
      if (requestResult.reject) {
        requestList.value[index].meta.error = requestResult.reject.message
      }

      return requestResult
    }))

    if (requestList.value.find(request => request.meta.error)) {
      return { reject: 'Ошибка в заявках' }
    }

    return {}
  }

  async function createMultiOrder(confirmationPhone: string, phone?: any) {
    let success = true
    for (const order of requestAsOrderList.value) {
      const index = order.meta.index
      requestList.value[index].meta.loading = true

      const {id, number, reject} = await orderCreate({
        ...pricePayload(order),
        confirmedCreateUserPhone: confirmationPhone,
        phone,
        context: {
          ...(order.context || {}),
          multiOrder: true
        }
      }).finally(() => {
        requestList.value[index].meta.loading = false
      })

      if (reject) {
        requestList.value[index].meta.error = reject.message
        success = false
      }
      if (id) {
        requestList.value[index].meta.id = id
        requestList.value[index].meta.number = number
      }
    }

    setToStorage()

    if (success) {
      storageMultiOrderData.value = null
      $showToast({
        variant: 'success',
        title: 'Заказы сформированы',
        autocloseDelay: multiPickupRoute.value ? 14000 : 8000,
        buttonText: multiPickupRoute.value ? 'Оформить групповой забор' : '',
        buttonCB: () => {
          if (multiPickupRoute.value) {
            useRouter().push(multiPickupRoute.value)
          }
        }
      })
    }
  }

  async function updateMultiOrderTimetable(splitTime: number) {
    const ttList: Timetable[] = []
    timetableCalcIndex.value = 0
    for (const order of requestAsOrderList.value) {
      const index = order.meta.index
      if (timetableCalcIndex.value !== order.meta.index) {
        return
      } else {
        timetableCalcIndex.value++
      }
      requestAsOrderList.value[index].meta.loading = true
      const timetablePayload = getTimetablePayload(order)
      const timetableData = await getTimeTableApi(timetablePayload)
      const tt = convertTimeTableV3(timetableData)
      const splitIndex = getSplitIndex(tt, splitTime)

      const destinationDate = tt?.destination?.[splitIndex]?.dates?.find(d => d.isDefault) as TimetableDate
      if (destinationDate) {
        order.directions.destination.calendar.date = formatDate(destinationDate.date, 'YYYY-MM-DD')
        order.directions.destination.calendar.from = formatDate(destinationDate.optimalTime.from, 'HH:mm')
        order.directions.destination.calendar.to = formatDate(destinationDate.optimalTime.to, 'HH:mm')
        order.directions.destination.calendar.terminalTime = formatDate(destinationDate.optimalTime.from, 'HH:mm')
        // Устанавливаем изменённые даты
        requestList.value[index] = order
      }
      ttList.push(tt)

      await getRequestPrice(index)
    }

    const splitValues = ttList.map(tt => tt.destination.map(destinationTT => destinationTT.groupTimeMin)).flat()
    timetableSplitList.value = Array.from(new Set(splitValues)).sort()
  }

  function clear() {
    requestList.value = []
    selectedRequestId.value = 0
    editMultiOrderMode.value = true
    timetableSplitList.value = []
    timetableCalcIndex.value = 0
  }

  return {
    locked,
    editMultiOrderMode,
    selectedRequest,
    selectedRequestId,
    requestList,
    storageMultiOrderData,
    priceList,
    totalPriceList,
    timetableSplitList,
    multiPickupRoute,

    addRequest,
    updateRequest,
    deleteRequest,
    saveRequest,
    getMultiPrice,
    setToStorage,
    getFromStorage,
    setData,
    sendMultiFormTest,
    createMultiOrder,
    updateMultiOrderTimetable,
    clear,
  }
}

export default useMultiOrder